import { useCallback } from 'react';
import { Select, TInputSize } from '@treezor/design-system';

import { useTranslation } from 'react-i18next';

import { IPostCreateUser } from '@/features/user/contexts/CreateUserContext';
import { useFieldOptions } from '@/features/user/hooks/createUser/useFieldOptions';

import { BooleanRadio } from './BooleanRadio';

interface ISanctionEmbargoQuestionaire {
  defaultValue?: {
    entitySanction?: number;
    activityOutsideEu?: string;
    economicSanctions?: string;
    residentCountriesSanctions?: string;
    involvedSanctions?: string;
  };
  handleValueChange: (value: number | string, key: keyof IPostCreateUser) => void;
  size?: TInputSize;
}

export function SanctionEmbargoQuestionaire({
  defaultValue,
  handleValueChange,
  size = 'm',
}: ISanctionEmbargoQuestionaire) {
  const { t } = useTranslation();

  const { sanctionEmbargoQuestionaireOptions } = useFieldOptions();

  const handleActivityOutsideEu = useCallback(
    (value: string) => {
      handleValueChange(value, 'activityOutsideEu');
      if (value === '0') {
        handleValueChange('0', 'economicSanctions');
        handleValueChange('0', 'residentCountriesSanctions');
        handleValueChange('0', 'involvedSanctions');
      }
    },
    [handleValueChange],
  );

  return (
    <div className="flex flex-col gap-6">
      <h6 className="typo-h6-bold">{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.title')}</h6>
      <span className="typo-body-regular-paragraph">
        {t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.description')}
      </span>
      <Select
        label={t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.entitySanction')}
        options={sanctionEmbargoQuestionaireOptions}
        defaultValue={defaultValue?.entitySanction}
        onChange={(value: number) => handleValueChange(value, 'entitySanctionsQuestionnaire')}
        size={size}
        testId="select-kyc-entity-sanction"
      />
      <BooleanRadio
        label={t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.activityOutsideEu')}
        name="activityOutsideEu"
        value={defaultValue?.activityOutsideEu}
        handleRadioChange={handleActivityOutsideEu}
        testId="radio-kyc-activity-outside-eu"
      />
      {defaultValue?.activityOutsideEu === '1' && (
        <>
          <BooleanRadio
            label={t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.economicSanctions')}
            name="economicSanctions"
            value={defaultValue?.economicSanctions}
            handleRadioChange={handleValueChange}
            testId="radio-kyc-economie-sanctions"
          />
          <BooleanRadio
            label={t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.residentCountriesSanctions')}
            name="residentCountriesSanctions"
            value={defaultValue?.residentCountriesSanctions}
            handleRadioChange={handleValueChange}
            testId="radio-kyc-resident-countries-sanctions"
          />
          <BooleanRadio
            customLabel={
              <div className="typo-body-medium-paragraph">
                <p>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctions')}</p>
                <ul className="list-disc ml-4">
                  <li>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctionItem1')}</li>
                  <li>{t('pages.dashboard.createUser.form.sanctionEmbargoQuestionnaire.involvedSanctionItem2')}</li>
                </ul>
              </div>
            }
            name="involvedSanctions"
            value={defaultValue?.involvedSanctions}
            handleRadioChange={handleValueChange}
            testId="radio-kyc-involved-sanctions"
          />
        </>
      )}
    </div>
  );
}
